import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, ArrowUUpLeft } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { money, fdate } from "@/lib/format";
import { PageHeader, EmptyState, StatusBadge, StatCard } from "@/components/common";
import { TextField, SelectField, AreaField } from "@/components/fields";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const TIPOS_APLIC = [["devolucion_total", "Devolución total"], ["devolucion_parcial", "Devolución parcial"], ["renta", "Aplicar a renta"], ["danos", "Aplicar a daños"], ["servicios", "Aplicar a servicios"], ["otros", "Otros adeudos"]].map(([value, label]) => ({ value, label }));
const EMPTY = { contract_id: "", owner_id: "", property_id: "", tenant_id: "", importe: "", fecha_recepcion: new Date().toISOString().slice(0, 10), resguarda: "", resguardo_tipo: "inmobiliaria", account_id: "", nota: "" };

export default function Depositos() {
  const [items, setItems] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [aplicOpen, setAplicOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [aplic, setAplic] = useState({ tipo: "devolucion_total", monto: "", nota: "" });

  const load = () => {
    api.get("/deposits").then((r) => setItems(r.data));
    api.get("/contracts").then((r) => setContracts(r.data));
    api.get("/accounts").then((r) => setAccounts(r.data));
  };
  useEffect(() => { load(); }, []);

  const pickContract = (cid) => {
    const c = contracts.find((x) => x.id === cid);
    setForm((f) => ({ ...f, contract_id: cid, owner_id: c?.owner_id || "", property_id: c?.property_id || "", tenant_id: c?.tenant_id || "", importe: f.importe || c?.deposito || "" }));
  };

  const save = async () => {
    if (!form.importe || form.importe <= 0) return toast.error("Importe inválido");
    try {
      await api.post("/deposits", { ...form, importe: parseFloat(form.importe), contract_id: form.contract_id || null, owner_id: form.owner_id || null, property_id: form.property_id || null, tenant_id: form.tenant_id || null, account_id: form.account_id || null });
      toast.success("Depósito registrado en resguardo"); setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openAplic = (d) => { setActive(d); setAplic({ tipo: "devolucion_total", monto: d.saldo_resguardo, nota: "" }); setAplicOpen(true); };
  const saveAplic = async () => {
    if (!aplic.monto || aplic.monto <= 0) return toast.error("Monto inválido");
    try {
      await api.post(`/deposits/${active.id}/aplicar`, { ...aplic, monto: parseFloat(aplic.monto) });
      toast.success("Aplicación registrada"); setAplicOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => { if (!window.confirm("¿Eliminar depósito?")) return; await api.delete(`/deposits/${id}`); toast.success("Eliminado"); load(); };

  const enResguardo = items.filter((d) => d.estado !== "devuelto").reduce((s, d) => s + (d.saldo_resguardo || 0), 0);
  const totalRecibido = items.reduce((s, d) => s + (d.importe || 0), 0);

  return (
    <div data-testid="depositos-page">
      <PageHeader title="Depósitos de garantía" subtitle="Dinero en resguardo, separado de los ingresos" actions={<Button data-testid="new-deposit-btn" onClick={() => { setForm(EMPTY); setOpen(true); }} className="rounded-sm gap-2"><Plus size={16} /> Nuevo depósito</Button>} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatCard label="En resguardo" value={money(enResguardo)} tone="warn" testId="total-resguardo" />
        <StatCard label="Total recibido" value={money(totalRecibido)} />
        <StatCard label="Depósitos activos" value={items.filter((d) => d.estado !== "devuelto").length} />
      </div>

      {items.length === 0 ? (
        <EmptyState title="Sin depósitos de garantía" hint="Registra el depósito recibido al iniciar un contrato." />
      ) : (
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <Table>
            <TableHeader><TableRow><TableHead>Inmueble</TableHead><TableHead>Arrendatario</TableHead><TableHead>Recepción</TableHead><TableHead>Resguarda</TableHead><TableHead>Tipo</TableHead><TableHead className="text-right">Importe</TableHead><TableHead className="text-right">En resguardo</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((d) => (
                <TableRow key={d.id} data-testid={`deposit-row-${d.id}`}>
                  <TableCell className="font-medium">{d.property_nombre || "—"}</TableCell>
                  <TableCell>{d.tenant_nombre || "—"}</TableCell>
                  <TableCell className="text-xs">{fdate(d.fecha_recepcion)}</TableCell>
                  <TableCell>{d.resguarda || d.account_nombre || "—"}</TableCell>
                  <TableCell className="capitalize">{d.resguardo_tipo || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{money(d.importe)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{money(d.saldo_resguardo)}</TableCell>
                  <TableCell><StatusBadge estado={d.estado} /></TableCell>
                  <TableCell className="text-right">
                    {d.estado !== "devuelto" && d.saldo_resguardo > 0 && (
                      <Button size="sm" variant="outline" className="rounded-sm gap-1" data-testid={`aplicar-btn-${d.id}`} onClick={() => openAplic(d)}><ArrowUUpLeft size={14} /> Aplicar</Button>
                    )}
                    <Button variant="ghost" size="icon" className="rounded-sm text-red-600" onClick={() => del(d.id)}><Trash size={16} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuevo depósito en garantía</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <SelectField label="Contrato (opcional, autocompleta)" value={form.contract_id} onChange={pickContract} testId="deposit-contract" options={contracts.map((c) => ({ value: c.id, label: `${c.property_nombre} · ${c.tenant_nombre}` }))} col={2} />
            <TextField label="Importe" type="number" value={form.importe} onChange={(v) => setForm((f) => ({ ...f, importe: v }))} testId="deposit-importe" />
            <TextField label="Fecha de recepción" type="date" value={form.fecha_recepcion} onChange={(v) => setForm((f) => ({ ...f, fecha_recepcion: v }))} />
            <SelectField label="Resguardo" value={form.resguardo_tipo} onChange={(v) => setForm((f) => ({ ...f, resguardo_tipo: v }))} testId="deposit-resguardo" options={[{ value: "inmobiliaria", label: "A resguardo de la inmobiliaria" }, { value: "propietario", label: "A resguardo del propietario" }]} col={2} />
            <TextField label="Quién lo resguarda" value={form.resguarda} onChange={(v) => setForm((f) => ({ ...f, resguarda: v }))} col={2} />
            <SelectField label="Cuenta donde se deposita (opcional)" value={form.account_id} onChange={(v) => setForm((f) => ({ ...f, account_id: v }))} testId="deposit-account" options={accounts.map((a) => ({ value: a.id, label: `${a.nombre} (${a.proposito})` }))} col={2} />
            <AreaField label="Nota" value={form.nota} onChange={(v) => setForm((f) => ({ ...f, nota: v }))} />
          </div>
          <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancelar</Button><Button className="rounded-sm" data-testid="save-deposit-btn" onClick={save}>Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={aplicOpen} onOpenChange={setAplicOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle>Aplicar depósito</DialogTitle></DialogHeader>
          {active && <p className="text-sm text-muted-foreground">En resguardo: <span className="font-bold tabular-nums text-foreground">{money(active.saldo_resguardo)}</span></p>}
          <div className="grid gap-3 mt-2">
            <SelectField label="Tipo de aplicación" value={aplic.tipo} onChange={(v) => setAplic((a) => ({ ...a, tipo: v }))} testId="aplic-tipo" options={TIPOS_APLIC} />
            <TextField label="Monto" type="number" value={aplic.monto} onChange={(v) => setAplic((a) => ({ ...a, monto: v }))} testId="aplic-monto" />
            <TextField label="Nota" value={aplic.nota} onChange={(v) => setAplic((a) => ({ ...a, nota: v }))} />
          </div>
          <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setAplicOpen(false)}>Cancelar</Button><Button className="rounded-sm" data-testid="save-aplic-btn" onClick={saveAplic}>Aplicar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
